import React from "react";
import { ChangeEvent, KeyboardEvent, MouseEvent } from "react";

import TagItem from "./TagItem";

import "./TagListInput.css";

interface ITagListInputProps {
    items?: any[];
    onItemsChange?: (items: any[]) => void;
    requestOptions: (search: string) => any[];
    displayMember?: ((item: any) => string) | string;
    allowCustom?: boolean;
    placeholder?: string;
    id?: string;
}

interface ITagListInputState {
    items: ISuggestionItem[];
    editingItem: string;
    filteredSuggestions: ISuggestionItem[];
}

interface ISuggestionItem {
    text: string;
    isHighlighted: boolean;
    item?: any;
}

// TODO: Look into changing TagListInput to not store it's own items and behave more like a textbox
// i.e. value prop = in, onChange event = out
class TagListInput extends React.Component<ITagListInputProps, ITagListInputState> {
    private dropdownRef: React.RefObject<HTMLDivElement>;
    private pendingListClose?: number;

    constructor(props: Readonly<ITagListInputProps>) {
        super(props);

        this.state = {
            editingItem: "",
            filteredSuggestions: [],
            items: [],
        };

        this.dropdownRef = React.createRef();

        this.handleKeypress = this.handleKeypress.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.removeTag = this.removeTag.bind(this);
        this.showDropdown = this.showDropdown.bind(this);
        this.hideDropdown = this.hideDropdown.bind(this);
        this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
        this.cancelPendingDropdownClose = this.cancelPendingDropdownClose.bind(this);
        this.queueDropdownClose = this.queueDropdownClose.bind(this);
    }

    public componentDidMount() {
        // this.updateSuggestions();
        if (this.props.items) {
            this.setState({
                items: this.convertNewSuggestions(this.props.items)
            });
        }
    }

    public componentDidUpdate(prevProps: ITagListInputProps, prevState: ITagListInputState) {
        if (prevProps.items !== this.props.items && this.props.items) {
            this.setState({
                items: this.convertNewSuggestions(this.props.items)
            });
        }
    }

    public render() {
        return (
            <div className="input-tag-list">
                {
                    this.state.items.map((v, i) => (
                        <TagItem key={v.text} text={v.text} id={i.toString()} onTagDelete={this.removeTag} />
                    ))
                }
                <input id={this.props.id} type="search" placeholder={this.props.placeholder ? this.props.placeholder : "Search..."} value={this.state.editingItem}
                    onChange={this.handleChange} onKeyDown={this.handleKeydown} onKeyPress={this.handleKeypress}
                    onFocus={this.showDropdown} onBlur={this.queueDropdownClose}
                    autoComplete="off_please_chrome" />
                {/* TODO: Revisit <datalist> once browser support is better (i.e. iOS supports it)
                <datalist id="nameList">
                    {this.state.suggestionItems.map(a => <option key={a} value={a} />)}
                </datalist>
                */}

                <div ref={this.dropdownRef} className="input-tag-suggestion-box list-collapsed">
                    <ul className="input-tag-suggestion-list" onFocus={this.cancelPendingDropdownClose} onBlur={this.queueDropdownClose} onClick={this.handleSuggestionClick}>
                        {
                            this.state.filteredSuggestions.map(a => (
                                <li key={a.text} tabIndex={0} className={a.isHighlighted ? "input-tag-suggestion-hl" : ""}>{a.text}</li>
                            ))
                        }
                    </ul>
                </div>
            </div>
        );
    }

    public updateSuggestions() {
        this.setState({
            filteredSuggestions: this.convertNewSuggestions(this.props.requestOptions(""))
        });
    }

    private handleKeypress(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key !== "Enter") {
            return;
        }

        const currentHighlight = this.state.filteredSuggestions.findIndex(v => v.isHighlighted);
        let selectedItem = currentHighlight > -1 ? this.state.filteredSuggestions[currentHighlight] : undefined;

        if (!selectedItem) {
            // Try to find item that matches what's typed
            selectedItem = this.state.filteredSuggestions.find(a => a.text.toLowerCase() === this.state.editingItem.toLowerCase());
        }

        // Deselect all
        this.state.filteredSuggestions.forEach(a => a.isHighlighted = false);

        if (selectedItem) {
            this.addItem(selectedItem);
        }
        else if (!selectedItem && this.props.allowCustom) {
            this.addItem({ isHighlighted: false, item: this.state.editingItem, text: this.state.editingItem });
        }
    }

    private handleKeydown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Escape") {
            return;
        }

        if (e.key === "Escape") {
            this.hideDropdown();
            return;
        }

        const initialHighlight = this.state.filteredSuggestions.findIndex(v => v.isHighlighted);
        let currentHighlight = initialHighlight;
        // const selectedItem = currentHighlight > -1 ? this.state.filteredSuggestions[currentHighlight] : undefined;

        switch (e.key) {
            case "ArrowDown":
                currentHighlight = (currentHighlight + 1) % this.state.filteredSuggestions.length;
                break;
            case "ArrowUp":
                currentHighlight = (currentHighlight - 1 + this.state.filteredSuggestions.length) % this.state.filteredSuggestions.length;
                break;
            default:
                return;
        }

        // Change highlighted item
        this.setState(s => {
            if (initialHighlight > -1) {
                s.filteredSuggestions[initialHighlight].isHighlighted = false;
            }
            s.filteredSuggestions[currentHighlight].isHighlighted = true;

            return {
                editingItem: s.filteredSuggestions[currentHighlight].text,
                filteredSuggestions: s.filteredSuggestions
            };
        });

    }

    private handleChange(e: ChangeEvent<HTMLInputElement>) {
        const searchValue = e.currentTarget.value;
        const searchLower = searchValue.toLowerCase();

        this.showDropdown();

        this.setState(s => {
            return {
                editingItem: searchValue,
                filteredSuggestions: this.convertNewSuggestions(this.props.requestOptions(searchLower))
            }
        });
    }

    private removeTag(id: string) {
        const keyInt = parseInt(id, 10);

        this.setState(s => {
            s.items.splice(keyInt, 1);

            return { items: s.items };
        }, () => {
            if (this.props.onItemsChange) {
                this.props.onItemsChange(this.state.items.map(a => a.item));
            }
        });
    }

    private showDropdown() {
        this.cancelPendingDropdownClose();
        this.updateSuggestions();
        if (this.dropdownRef.current) {
            this.dropdownRef.current.classList.remove("list-collapsed");
        }
    }

    private hideDropdown() {
        if (this.dropdownRef.current) {
            this.dropdownRef.current.classList.add("list-collapsed");
        }
    }

    private handleSuggestionClick(e: MouseEvent<HTMLUListElement>) {
        for (let i = 0; i < e.currentTarget.children.length; i++) {
            if (e.target === e.currentTarget.children[i]) {
                this.addItem(this.state.filteredSuggestions[i]);

                this.hideDropdown();
                break;
            }
        }
    }

    private addItem(item: ISuggestionItem) {
        if (!item || this.state.items.find(a => a.text === item.text)) {
            return;
        }

        this.setState(s => {
            s.items.push(item);

            return {
                editingItem: "",
                filteredSuggestions: this.convertNewSuggestions(this.props.requestOptions("")),
                items: s.items
            };
        }, () => {
            if (this.props.onItemsChange) {
                this.props.onItemsChange(this.state.items.map(a => a.item));
            }
        });
    }

    private convertNewSuggestions(items: any[]) {
        if (!items) {
            return [];
        }

        const suggestions: ISuggestionItem[] = [];
        for (const item of items) {
            if (typeof this.props.displayMember === "string") {
                suggestions.push({ isHighlighted: false, item, text: item[this.props.displayMember] });
            }
            else if (typeof this.props.displayMember === "function") {
                suggestions.push({ isHighlighted: false, item, text: this.props.displayMember(item) });
            }
            else {
                suggestions.push({ isHighlighted: false, item, text: item.toString() });
            }
        }

        return suggestions;
    }

    private queueDropdownClose() {
        // NOTE: I would rather not use setTimeout(), but how it's used here should be okay
        // as we cancel the timeout of a list item becomes focused before the timeout occurs
        this.pendingListClose = window.setTimeout(() => this.hideDropdown(), 50);
    }

    private cancelPendingDropdownClose() {
        // Cancel pending list close
        clearTimeout(this.pendingListClose);
    }
}

export default TagListInput;
